import { Fragment, FunctionalComponent, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import style from "./style.scss";

import { WorkModel } from "models/WorkModel";
import { WorkPieceModel } from "models/WorkPieceModel";

import AudioPlayer from "components/audioPlayer";
import { Button } from "components/button";
import { CloseIcon } from "components/icons";
import GameMap from "components/gameMap";
import { Picture } from "components/picture";
import Vimeo from "components/vimeo";
import WorldMap from "components/worldMap";

import { COLOR_BLACK } from "utils/color";
import { Distance } from "utils/geo/geo";
import { ResourceContentType } from "utils/resource/resourceContentType";

interface Props {
  distance: Distance;
  work: WorkModel;
  pathToWork: WorkModel[];
  onDirectionChange: () => void;
}

const CLOSE_MAP_SCROLL_DELTA = 50;

const ExhibitWork: FunctionalComponent<Props> = ({
  distance,
  work,
  pathToWork,
  onDirectionChange,
}: Props) => {
  const [workPieces, setWorkPieces] = useState<WorkPieceModel[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [activeMobileMap, setActiveMobileMap] = useState<
    "game" | "world" | null
  >(null);
  const lastScrollPosition = useRef(0);
  const bannerTextLength =
    (work.artist?.fullName || "").length + (work.medium?.name || "").length;
  const pathParent = pathToWork[pathToWork.length - 2];
  const showReverseButton = !!(pathParent && work.parentWorks.length);

  useEffect(() => {
    if (isVisible) return;

    const revealTimeout = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    return () => {
      clearTimeout(revealTimeout);
    };
  });

  useEffect(() => {
    const maybeCloseMap = () => {
      if (!activeMobileMap) return;
      if (
        Math.abs(
          lastScrollPosition.current -
            (document.scrollingElement?.scrollTop ?? 0)
        ) > CLOSE_MAP_SCROLL_DELTA
      ) {
        setActiveMobileMap(null);
      }
    };

    window.addEventListener("scroll", maybeCloseMap);

    return () => {
      window.removeEventListener("scroll", maybeCloseMap);
    };
  });

  useEffect(() => {
    work
      .loadPieces(["other"])
      .then((pieces) => {
        setWorkPieces(pieces);
      })
      .catch(() => {
        console.error("failed to fetch pieces");
      });
  }, [work]);

  return (
    <article
      class={style.root}
      data-active-mobile-map={activeMobileMap}
      data-visible={String(isVisible)}
    >
      <div class={style.workContainer}>
        <div class={style.banner} data-character-count={bannerTextLength}>
          <h2>
            <span class={style.bannerFirstName}>{work.artist?.firstName}</span>
            <span class={style.bannerLastName}>{work.artist?.lastName}</span>
            <span class={style.bannerDivider} />
            <span class={style.bannerMedium}>{work.medium?.name}</span>
          </h2>
        </div>
        <div class={style.workPreview}>
          <div class={style.workImage}>
            {getPreviewComponentFromWorkPieces(workPieces, work)}
          </div>
          <div class={style.workDetails}>
            <div class={style.workDetailsLocationInfo}>
              <div>{work.artist?.formattedLocation}</div>
              <div>
                Path distance: {distance.miles.toLocaleString()} mi /{" "}
                {distance.kilometers.toLocaleString()} km
              </div>
            </div>
            <div class={style.mobileActions}>
              {work.link ? (
                <Button tag="a" href={work.link} color="black">
                  View Work
                </Button>
              ) : null}
              {showReverseButton ? (
                <Button tag="button" color="black" onClick={onDirectionChange}>
                  Reverse
                </Button>
              ) : null}
              <Button
                tag="button"
                color="black"
                style="outline"
                onClick={() => {
                  setActiveMobileMap("game");
                  lastScrollPosition.current =
                    document.scrollingElement?.scrollTop ?? 0;
                }}
              >
                Game Map
              </Button>
              <Button
                tag="button"
                color="black"
                style="outline"
                onClick={() => {
                  setActiveMobileMap("world");
                  lastScrollPosition.current =
                    document.scrollingElement?.scrollTop ?? 0;
                }}
              >
                Global Map
              </Button>
            </div>
            <div class={style.desktopActions}>
              {showReverseButton ? (
                <Button tag="button" color="black" onClick={onDirectionChange}>
                  Reverse
                </Button>
              ) : null}
              {work.link ? (
                <Button tag="a" href={work.link} color="black">
                  View Work
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        <div class={style.gameMap}>
          <GameMap
            centerWork={work}
            className={style.gameMapGraphic}
            depth={pathParent ? 2 : 1}
            getNodeStyle={(workNode) => {
              if (workNode === work) return "highlight";
              if (
                work.parentWorks.includes(workNode) ||
                work.childWorks.includes(workNode)
              ) {
                return "normal";
              }
              return "deemphasize";
            }}
            getPathStyle={(fromWork, toWork) => {
              if (
                pathToWork.includes(fromWork) &&
                pathToWork.includes(toWork)
              ) {
                return "highlight";
              }

              return "deemphasize";
            }}
            showZoomControls={false}
            startingWork={pathParent ?? work}
          />
          <button
            aria-label="Close popup"
            class={style.mapCloseButton}
            type="button"
            onClick={() => setActiveMobileMap(null)}
          >
            <CloseIcon />
          </button>
        </div>
        <div class={style.worldMap}>
          {work.artist ? (
            <WorldMap
              className={style.worldMapGraphic}
              fitBounds={true}
              hideAttribution={true}
              markers={pathToWork
                .filter(
                  (pathWork) =>
                    pathWork.artist?.latitude && pathWork.artist.longitude
                )
                .map((pathWork, i) => ({
                  lat: pathWork.artist?.latitude ?? 0,
                  lng: pathWork.artist?.longitude ?? 0,
                  color: pathWork === work ? "lightgreen" : "black",
                  options: {
                    zIndexOffset: i === pathToWork.length - 1 ? 1000 : 0,
                  },
                }))}
              lines={[
                {
                  coordinates: pathToWork
                    .filter(
                      (work) => work.artist?.latitude && work.artist.longitude
                    )
                    .map((work) => ({
                      lat: work.artist?.latitude ?? 0,
                      lng: work.artist?.longitude ?? 0,
                    })),
                  options: {
                    color: COLOR_BLACK,
                    dashArray: "4",
                    weight: 1,
                  },
                },
              ]}
              options={{
                zoomControl: false,
                zoom: 1,
              }}
            />
          ) : null}
          <button
            aria-label="Close popup"
            class={style.mapCloseButton}
            type="button"
            onClick={() => setActiveMobileMap(null)}
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    </article>
  );
};

function getPreviewComponentFromWorkPieces(
  pieces: WorkPieceModel[],
  work: WorkModel
) {
  const audio = pieces.filter(
    (piece) => piece.contentType === ResourceContentType.AUDIO_FILE
  );
  const images = pieces.filter(
    (piece) => piece.contentType === ResourceContentType.IMAGE_FILE
  );
  const vimeoVideos = pieces.filter(
    (piece) => piece.contentType === ResourceContentType.VIMEO
  );

  if (vimeoVideos.length && vimeoVideos[0].vimeoUrl) {
    return (
      <div class={style.workPreviewVimeoPlayer}>
        <Vimeo
          playerOptions={{
            controls: false,
          }}
          url={vimeoVideos[0].vimeoUrl}
        />
      </div>
    );
  }

  if (audio.length && audio[0].upload) {
    return (
      <Fragment>
        <img
          src={`/assets/img/fallback-work-image${getFallbackWorkImageNumber(
            work
          )}.jpg`}
          alt=""
        />
        <div class={style.workPreviewAudioPlayer}>
          <AudioPlayer src={audio[0].upload} showProgressBar={false} />
        </div>
      </Fragment>
    );
  }

  if (images.length && images[0].upload) {
    return <Picture src={images[0].upload} alt="" size="w800" />;
  }

  return (
    <div>
      <img
        src={`/assets/img/fallback-work-image${getFallbackWorkImageNumber(
          work
        )}.jpg`}
        alt=""
      />
      {work.isOriginalMessage ? (
        <p class={style.workPreviewMessage}>
          Alexander&apos;s men were certainly the first Europeans known to have
          seen a banyan tree. They were amazed. The vivid descriptions they
          wrote would inform Theophrastus, the father of modern botany, back
          home in Greece. More than a thousand years earlier, the people of the
          Indus Valley Civilization adopted a stylized banyan as a symbol in
          their script.
        </p>
      ) : (
        <h2 class={style.workPreviewTitle}>
          {work.title ? work.title : "Untitled"}
        </h2>
      )}
    </div>
  );
}

const worksWithAssignedFallbacks = new Map<string, number>();

let nextFallbackWorkImageNumber = 1;

function getFallbackWorkImageNumber(work: WorkModel) {
  const existingAssignment = worksWithAssignedFallbacks.get(work.id);
  if (existingAssignment) return existingAssignment;
  const num = nextFallbackWorkImageNumber;
  worksWithAssignedFallbacks.set(work.id, num);
  nextFallbackWorkImageNumber = num === 1 ? 2 : 1;
  return num;
}

export default ExhibitWork;
